import { useEffect, useRef } from 'react';
import { Box, Paper, TextField, IconButton, Typography, CircularProgress } from '@mui/material';
import { Send as SendIcon, SmartToy as BotIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Ventana de chat compartida del bot de captación.
 * La usan el simulador interno (CaptationSimulator) y la página pública
 * (PublicCaptationChat). Solo presentación: los mensajes y el envío los
 * maneja el contenedor.
 *
 * Props:
 *  - messages: [{ role: 'bot'|'candidate', content }]
 *  - onSend: (text) => void
 *  - sending: bool (muestra indicador "escribiendo…" y bloquea input)
 *  - finished: bool (deshabilita el input definitivamente)
 *  - height: alto del área de mensajes (default '60vh')
 *  - fill: bool (el área de mensajes ocupa el alto disponible del contenedor
 *    flex en vez de `height`; lo usan las páginas públicas para que solo
 *    haga scroll el chat y nunca la página)
 *  - expectsDocument: bool (el flujo está en un paso documento → muestra el clip)
 *  - onSendFile: (file) => void (subida del archivo; la maneja el contenedor)
 */
/**
 * Negritas de los mensajes del bot (2026-10-08): el texto viene con el formato
 * de WhatsApp (*texto*) y, en algunos mensajes, con el de Markdown (**texto**).
 * Aquí se ve en negrita y sin los asteriscos. Como en WhatsApp, el texto entre
 * asteriscos no puede empezar ni terminar con un espacio ("5 * 3 = 15 * 2"
 * queda igual). Lo que escribe la persona se muestra tal cual.
 */
const renderBotText = (content) => {
  const text = String(content ?? '');
  const bold = /\*\*([^*\s](?:[^*\n]*[^*\s])?)\*\*|\*([^*\s](?:[^*\n]*[^*\s])?)\*/g;
  const parts = [];
  let last = 0;
  let match = bold.exec(text);
  while (match) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={match.index}>{match[1] || match[2]}</strong>);
    last = match.index + match[0].length;
    match = bold.exec(text);
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const CaptationChatWindow = ({ messages, onSend, sending = false, finished = false, height = '60vh', fill = false, expectsDocument = false, onSendFile = null }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // permitir re-seleccionar el mismo archivo
    if (file && onSendFile && !sending && !finished) onSendFile(file);
  };

  // Bajar al último mensaje desplazando SOLO el área de mensajes:
  // scrollIntoView movía además la página (y el ATS, en el simulador)
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
    } else {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending || finished) return;
    setDraft('');
    onSend(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...(fill ? { flex: 1, minHeight: 0 } : {}) }}>
      <Box ref={listRef} sx={{ ...(fill ? { flex: 1, minHeight: 0 } : { height }), overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: 'grey.50' }}>
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'candidate' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            {msg.role === 'bot' && (
              <BotIcon sx={{ fontSize: 20, color: 'primary.main', mb: 0.5 }} />
            )}
            <Box
              sx={{
                maxWidth: '75%',
                px: 2,
                py: 1.2,
                borderRadius: 3,
                borderBottomLeftRadius: msg.role === 'bot' ? 0.5 : 3,
                borderBottomRightRadius: msg.role === 'candidate' ? 0.5 : 3,
                bgcolor: msg.role === 'candidate' ? 'primary.main' : 'background.paper',
                color: msg.role === 'candidate' ? 'primary.contrastText' : 'text.primary',
                boxShadow: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Typography variant="body2">
                {msg.role === 'bot' ? renderBotText(msg.content) : msg.content}
              </Typography>
            </Box>
          </Box>
        ))}

        {sending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box sx={{ px: 2, py: 1.2, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {t('ats.captation.chat.typing')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        {expectsDocument && onSendFile && !finished && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,image/jpeg,image/png"
              onChange={handleFileChange}
            />
            <Tooltip title={t('ats.captation.chat.attach')}>
              <IconButton
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                aria-label={t('ats.captation.chat.attach')}
              >
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={finished ? t('ats.captation.chat.finished') : t('ats.captation.chat.inputPlaceholder')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          // Mientras el bot responde el input NO se deshabilita (mantiene el foco
          // y permite seguir escribiendo); solo se bloquea el envío.
          disabled={finished}
          autoFocus={!finished}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!draft.trim() || sending || finished}
          aria-label={t('ats.captation.chat.send')}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default CaptationChatWindow;
